import { Component, OnDestroy, OnInit } from '@angular/core';

import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController, NavController, PopoverController } from '@ionic/angular';
import { Observable, Subject } from 'rxjs';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AnalyticsServicesNameEnum } from 'src/app/core/services/analytics/enums/analytics-services-name.enum';
import { TIME_OF_DAY_DESCRIPTION } from 'src/app/pages/agreements/constants/rate-description.constants';
import {
  PriceLimitAlertEnum,
  PriceLimitAlertToDEnum,
} from 'src/app/pages/agreements/enums/price-limit-alert-type.enum';
import { BasePage } from 'src/app/pages/base.page';
import { LastMinuteJobRateFormPropertiesToUpdate } from 'src/app/pages/flex/pages/jobs/types/lmj-minute-job-rate-form-update';
import { FlexJobsService } from 'src/app/pages/flex/services/flex-jobs.service';
import { EventTrackingEnum } from 'src/app/shared/enums/event-tracking.enum';
import { GQLErrorCodeEnum } from 'src/app/shared/enums/graphql-error-code.enum';
import { JobDetail } from 'src/app/shared/models/gql/job-detail.model';
import { BudgetSupportType } from 'src/app/shared/models/gql/rate.model';
import { NetRate, PriceLimitAlertData } from 'src/app/shared/models/price-limit-alert-data.model';
import { AccountService } from 'src/app/shared/services/account.service';
import { JobsService } from 'src/app/shared/services/jobs.service';
import { MinimumRateService } from 'src/app/shared/services/minimum-rate.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { isGraphQLErrorCode } from 'src/app/shared/utilities/graphql-error-handling';

@Component({
  selector: 'app-carer-open-job',
  templateUrl: './carer.page.html',
  styleUrls: ['./carer.page.scss'],
})
export class CarerOpenJobPage extends BasePage implements OnInit, OnDestroy {
  public job: JobDetail;

  public showOfferBlock = false;

  form: UntypedFormGroup;

  // data to be passed to the price limit alert component for segment event use
  priceLimitAlertData: PriceLimitAlertData;

  // set minimum rate of agreement SP can provide
  minimumRate: number;

  // Checks whether the curent user type is a carer
  isCarer = true;

  // default service category
  defaultServiceCategory$: Observable<BudgetSupportType[]>;

  /** Subject used to unsubcribe from the service FormGroup status chagne. */
  private destroySub$ = new Subject();

  constructor(
    public flexJobsService: FlexJobsService,
    public accountService: AccountService,
    public navCtrl: NavController,
    private route: ActivatedRoute,
    protected alertCtrl: AlertController,
    protected loadingCtrl: LoadingController,
    public popoverCtrl: PopoverController,
    public jobsService: JobsService,
    private utilityService: UtilityService,
    private router: Router,
    protected fb: UntypedFormBuilder,
    private analyticsService: AnalyticsService,
    private minimumRateService: MinimumRateService,
  ) {
    super('CarerOpenJobPage');
  }

  async ngOnInit(): Promise<void> {
    this.getMinimumAgreementRate();
    this.setUpForm();
    this.isCarer = this.accountService.userIs('carer');
    // get job details
    await this.getJobDetails().then(() => {
      this.analyticsService.track(
        EventTrackingEnum.JOB_VIEWED,
        { job_id: this.job?.id?.toString() },
        AnalyticsServicesNameEnum.SEGMENT_SERVICE,
      );
    });
    const workerType = this.job?.workerFields?.match?.user?.profile?.myType;
    this.priceLimitAlertData = {
      agreementType: 'LMJ',
      agreementId: this.job?.workerFields?.agreement?.id,
      clientId: this.job?.client?.id,
      legacyAgreement: false,
      workerType: Array.isArray(workerType) ? workerType.toString() : workerType,
    };

    this.setDefaultServiceCategory();
  }

  ngOnDestroy() {
    this.destroySub$.next(null);
    this.destroySub$.complete();
  }

  /**
   * Setup form group.
   */
  setUpForm() {
    this.form = this.fb.group({
      comment: ['', Validators.required],
      rate: [
        '',
        [
          Validators.required,
          Validators.pattern('^[0-9]+(.[0-9]{0,2})?$'),
          Validators.min(this.minimumRate),
          Validators.max(9999.99),
        ],
      ],
      netRate: [''],
      showCapRateBanner: [false],
      priceLimitAlertType: [''],
      dayOfWeekSelected: [''],
      timeOfDaySelected: [''],
      capRate: [''],
    });
  }

  /**
   * Send last minute job offer
   *
   */
  async offer() {
    if (!this.form.value.comment) {
      this.showAlert('Invalid Offer', 'Please type a response');
    } else if (!this.form.value.rate || isNaN(this.form.value.rate) || this.form.value.rate < 1) {
      this.showAlert('Invalid Offer', 'Please enter valid offer amount');
    } else if (this.form.value.rate < this.minimumRate) {
      this.showAlert(
        'Invalid Offer',
        `The current minimum rate is $${this.minimumRate} based on our terms of use. Please update your offer.`,
      );
    } else {
      this.showLoading();
      try {
        const res = await this.flexJobsService.flexJobWorkerAction({
          id: this.job?.id,
          flexMatchAttributes: { comment: this.form.value.comment, rateOffer: this.form.value.rate * 100 },
          action: 'offer',
        });
        if (res) {
          // refresh the job list
          this.jobsService.refreshJobList();
          // refetch the job details
          await this.getJobDetails();
          this.showOfferBlock = false;
        }
      } catch (error) {
        if (isGraphQLErrorCode(error, GQLErrorCodeEnum.ACTOR_SUSPENDED)) {
          // If actor is soft ban than return to root page.
          this.router.navigateByUrl('/');
        } else if (isGraphQLErrorCode(error, GQLErrorCodeEnum.TARGET_SUSPENDED)) {
          this.utilityService.showAlert(
            'Job unavailable',
            'This job is no longer available. Please try responding to another job posting.',
          );
        } else {
          this.error(error);
        }
      } finally {
        this.hideLoading();
      }
    }
  }

  /**
   * Decline last minute job
   *
   */
  async decline() {
    this.showLoading();
    try {
      const res = await this.flexJobsService.flexJobWorkerAction({
        id: this.job?.id,
        action: 'decline',
      });
      if (res) {
        // refresh the job list
        this.jobsService.refreshJobList();
        // refetch the job details
        await this.getJobDetails();
      }
    } catch (error) {
      this.error(error);
    } finally {
      this.hideLoading();
    }
  }

  async withdraw() {
    await this.showConfirmationPopover(
      {
        header: {
          text: 'Withdraw Offer?',
          level: 'h4',
        },
        description: [
          'It will be deleted and removed from the client’s offer list.',
          'There is no penalty for withdrawing an offer that has not been accepted.',
        ],
        buttons: [
          {
            text: 'Cancel',
            type: 'outline',
            handler: () => this.popoverCtrl.dismiss(),
          },
          {
            text: 'Withdraw Offer',
            handler: () => {
              this.popoverCtrl.dismiss({ withdraw: true });
            },
          },
        ],
      },
      async (response) => {
        if (response.data && response.data.withdraw) {
          this.showLoading();
          try {
            const res = await this.flexJobsService.flexJobWorkerAction({
              id: this.job?.id,
              action: 'withdraw',
            });
            if (res) {
              // refresh the job list
              this.jobsService.refreshJobList();
              // refetch the job details
              await this.getJobDetails();
            }
          } catch (error) {
            this.error(error);
          } finally {
            this.hideLoading();
          }
        }
      },
    );
  }

  error(error) {
    let errorHeading = 'Error';
    let errorMessage = 'Unable to perform action. Please try again later';

    if (error?.graphQLErrors[0]?.extensions?.code === 'VALIDATION_ERROR') {
      errorHeading = 'Invalid Offer';
      errorMessage = error?.graphQLErrors[0]?.extensions?.validation;
    }

    this.showAlert(errorHeading, errorMessage);
    console.log(error);
  }

  close() {
    this.navCtrl.navigateRoot('/tabs/jobs');
  }

  /**
   * Get job details.
   *
   */
  private async getJobDetails() {
    const { paramMap } = this.route.snapshot;
    const jobId = Number(paramMap.get('id'));
    try {
      this.job = await this.jobsService.getPostedJobDetailsById(jobId);
      if (this.job?.workerFields?.match?.amount) {
        this.form.controls.rate.setValue(this.job.workerFields.match.amount);
        await this.getCalculatedRate();
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Calculate rate on price changes
   *
   */
  async getCalculatedRate(): Promise<void> {
    try {
      // Avoid api call in case of invalid field data
      if (!this.form.get('rate').valid) {
        this.form.patchValue({
          showCapRateBanner: false,
        });
        return;
      }
      const obj = {
        baseRate: parseFloat(this.form.value.rate),
        consumerId: this.job.client.id,
        timeAndPlace: {
          startTime: this.job.timestamps.startTime,
          endTime: this.job.timestamps.endTime,
          postCodeGeoId: this.job.location.postCodeGeoId,
        },
      };

      const netRate: NetRate = await this.jobsService.getLMJRates(obj);
      // check if net rate is exists
      if (netRate) {
        // get rate controls and update the rate from api response
        this.form.patchValue({
          netRate: netRate.rateWithFee,
          showCapRateBanner: netRate.exceedCapRate,
          priceLimitAlertType: this.getPriceLimitAlertType(netRate.priceLimitAlertType, netRate.priceLimitAlertToDType),
          capRate: netRate.capRate,
          dayOfWeekSelected: netRate.priceLimitAlertType,
          timeOfDaySelected: netRate.priceLimitAlertToDType,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Updates the form based on the updated properties from the rate child compoment.
   *
   * @param propertiesToUpdate - properties to update.
   */
  updateFormProperties(propertiesToUpdate: LastMinuteJobRateFormPropertiesToUpdate): void {
    const { rate, netRate, capRate, dayOfWeekSelected, timeOfDaySelected } = propertiesToUpdate;
    this.form.patchValue({
      rate,
      netRate,
      capRate,
      dayOfWeekSelected,
      timeOfDaySelected,
    });
  }

  /**
   * Returns a string representing what should be displayed on the price limit alert.
   *
   * @param dayOfWeek - priceLimitAlertType passed from the backend indicating day of the week.
   * @param timeOfDay - priceLimitAlertTodType passed from the backend indicating time of the day.
   * @returns - String representation of what the price limit alert type refers to.
   */
  private getPriceLimitAlertType(dayOfWeek: PriceLimitAlertEnum, timeOfDay: PriceLimitAlertToDEnum): string {
    if (timeOfDay) {
      return `${dayOfWeek}, ${TIME_OF_DAY_DESCRIPTION[timeOfDay.toLocaleLowerCase()]}.`;
    }

    return `${dayOfWeek}.` || 'Weekday between 6am - 8pm';
  }

  /**
   * Creates a stream responsible for fetching from the server the default service category.
   */
  private setDefaultServiceCategory(): void {
    this.defaultServiceCategory$ = this.flexJobsService.getDefaultServiceCategoryFromJob(
      this.job?.client?.id?.toString(),
      this.job?.id?.toString(),
    );
  }

  /**
   * Sets the minimum agreement rate.
   */
  private getMinimumAgreementRate(): void {
    this.minimumRateService.fetchMinimumAgreementRate$().subscribe((response) => (this.minimumRate = response));
  }
}
